import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";
import { scrollToSection } from "../utils/scrollToSection";

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  const handleNavClick = (id: string) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== "/") {
      // If not on homepage → navigate, then scroll after it loads
      navigate("/");
      setTimeout(() => scrollToSection(id), 300);
    } else {
      // Already on homepage → scroll directly
      scrollToSection(id);
    }
  };

  const navItems = ["home", "features", "contact", "faqs"];

  return (
    <header className="sticky top-0 z-30 w-full border-b border-white/40 bg-white/85 shadow-sm backdrop-blur-xl dark:border-slate-800/80 dark:bg-slate-950/85">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <button
          onClick={() => handleNavClick("home")}
          className="group flex items-center gap-3"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-lg font-black text-white shadow-lg shadow-primary/20 transition group-hover:-translate-y-0.5">
            H
          </span>
          <span className="text-xl font-black tracking-tight text-slate-950 dark:text-white">
            HovaStore
          </span>
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden items-center rounded-full border border-slate-200 bg-slate-50/90 p-1 md:flex dark:border-slate-800 dark:bg-slate-900/80">
          {navItems.map((key) => (
            <button
              key={key}
              onClick={() => handleNavClick(key)}
              className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-white hover:text-primary hover:shadow-sm dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              {t(`navbar.${key}`)}
            </button>
          ))}
          <Link
            to="/pricing-calculator"
            className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-white hover:text-primary hover:shadow-sm dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            Pricing
          </Link>
          <Link
            to={`/${i18n.language.split("-")[0]}/docs`}
            className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-white hover:text-primary hover:shadow-sm dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            {t("navbar.docs")}
          </Link>
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <div className="flex items-center rounded-full border border-slate-200 bg-slate-50/90 p-1 shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
            <label className="sr-only" htmlFor="desktop-language">
              Select language
            </label>
            <select
              id="desktop-language"
              onChange={(e) => changeLanguage(e.target.value)}
              defaultValue={i18n.language}
              className="h-10 rounded-full border-0 bg-transparent px-3 text-sm font-black text-slate-700 outline-none transition hover:bg-white hover:text-primary dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              <option value="en">EN</option>
              <option value="fr">FR</option>
              <option value="rw">RW</option>
            </select>
            <div className="mx-1 h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <ThemeToggle />
          </div>
          <button
            onClick={() => handleNavClick("learn-more")}
            className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-5 text-sm font-black text-white shadow-lg shadow-primary/20 transition hover:-translate-y-0.5 hover:bg-[#6d1f55]"
          >
            {t("navbar.learnMore")}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="rounded-lg border border-slate-200 p-2 text-slate-700 focus:outline-none dark:border-slate-700 dark:text-slate-100 md:hidden"
          aria-label="Toggle navigation menu"
        >
          {isMobileMenuOpen ? (
            <svg
              className="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              className="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      <nav
        className={`fixed left-0 w-full border-b border-slate-200 bg-white/95 shadow-xl backdrop-blur-xl transition-transform duration-300 ease-in-out dark:border-slate-800 dark:bg-slate-950/95 md:hidden ${
          isMobileMenuOpen
            ? "translate-y-0 top-[65px]"
            : "-translate-y-full top-0"
        }`}
      >
        <div className="space-y-2 px-4 py-4">
          {navItems.map((key) => (
            <button
              key={key}
              onClick={() => handleNavClick(key)}
              className="block w-full rounded-lg px-4 py-3 text-left font-semibold text-slate-700 hover:bg-primary/10 hover:text-primary dark:text-slate-200"
            >
              {t(`navbar.${key}`)}
            </button>
          ))}
          <Link
            to="/pricing-calculator"
            className="block rounded-lg px-4 py-3 font-semibold text-slate-700 hover:bg-primary/10 hover:text-primary dark:text-slate-200"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Pricing Calculator
          </Link>
          <Link
            to={`/${i18n.language.split("-")[0]}/docs`}
            className="block rounded-lg px-4 py-3 font-semibold text-slate-700 hover:bg-primary/10 hover:text-primary dark:text-slate-200"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {t("navbar.docs")}
          </Link>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              className="rounded-xl bg-primary px-4 py-3 text-center font-bold text-white"
              onClick={() => handleNavClick("learn-more")}
            >
              {t("navbar.learnMore")}
            </button>
            <select
              onChange={(e) => changeLanguage(e.target.value)}
              defaultValue={i18n.language}
              className="h-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-bold text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="en">🇬🇧 EN</option>
              <option value="fr">🇫🇷 FR</option>
              <option value="rw">🇷🇼 RW</option>
            </select>
          </div>
          <div className="px-4 py-2">
            <ThemeToggle />
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
